import type { Measure } from "../../components/index.ts";
import type { StatusLevel } from "../../components/index.ts";
import type {
  McpToolPage,
  McpToolSummary,
  Quantity,
  McpToolSurfaceDriftEntry,
  PassportState,
  GatewayAccessOutcome,
  GatewaySourceKind,
  GatewayMode,
} from "./use-mcp-gateway.ts";

/**
 * The shared presentational vocabulary for the MCP Gateway screen (design §10, requirement 13.2).
 *
 * It keeps the "how a contract value becomes a label/measure/level" rules in one testable place,
 * away from the components, so the tiles, the tools table, the drawer and the Tool-surface tab all
 * label numbers and states the same way — and so the design 12.3 rules ("counters are exact,
 * estimates say 'est.'"; "unverifiable is never green") are encoded once for this screen.
 */

/** Map a contract `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity | undefined | null): Measure {
  // A quantity with no data cannot be verified: neither an exact counter nor a sampled estimate.
  if (q === undefined || q === null || q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** The numeric value of a `Quantity`, or `null` when it carries no data. */
export function quantityValue(q: Quantity | undefined | null): number | null {
  return q === undefined || q === null ? null : q.value;
}

/** Format a `Quantity` as an integer-ish count string, with the honest em dash for no data. */
export function formatCount(q: Quantity | undefined | null): string {
  const v = quantityValue(q);
  if (v === null) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Format a `Quantity` as milliseconds, with the honest em dash for no data. */
export function formatMs(q: Quantity | undefined | null): string {
  const v = quantityValue(q);
  if (v === null) return "—";
  return `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
}

/** Format a `Quantity` holding a 0..1 ratio as a percentage, with the em dash for no data. */
export function formatRate(q: Quantity | undefined | null): string {
  const v = quantityValue(q);
  if (v === null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

/**
 * The five headline tiles for the MCP Gateway (requirement 13.2): forwarded calls, deny rate, tool
 * error rate, p95 latency, and unenforced calls. The page is an aggregate over the window; the
 * tiles roll the per-tool rows up so the operator sees the gateway at a glance.
 *
 * - calls: the sum of every tool's forwarded `calls`.
 * - deny rate: denied boundary decisions over (allowed + denied), folded from the outcome mix's
 *   `source: decision` categories. Null (unverifiable) when no boundary decisions were folded in.
 * - tool error rate: the call-weighted mean of each tool's `error_rate` (non-`ok` outcomes over
 *   total). The whole gateway's error rate is honestly weighted by how busy each tool was.
 * - p95 latency: the single busiest tool's p95 (percentiles do not sum, so the headline shows the
 *   worst-case path the operator is most likely to notice). Null when no tool reported a p95.
 * - unenforced calls: not derivable from the tools page alone — it is a tool-surface fact — so the
 *   caller passes the surface's `tools_unenforced` count through {@link gatewayTiles}'s override.
 */
export interface TileModel {
  readonly key: string;
  readonly label: string;
  /** Pre-formatted display value (never a fabricated number — "—" when there is no data). */
  readonly value: string;
  readonly measure: Measure;
  /** The confidence word for the accessible name. */
  readonly empty: boolean;
}

/** Sum every tool's forwarded calls; estimate if any tool's calls were sampled. */
export function totalCalls(page: McpToolPage): Quantity {
  let sum = 0;
  let any = false;
  let estimated = false;
  for (const t of page.items) {
    const v = quantityValue(t.calls);
    if (v !== null) {
      sum += v;
      any = true;
    }
    if (t.calls.exactness === "estimated") estimated = true;
  }
  return { value: any ? sum : null, exactness: estimated ? "estimated" : "exact" };
}

/** The folded allow/deny counts across every tool's outcome mix (source: decision categories). */
function decisionCounts(page: McpToolPage): { allowed: number; denied: number; any: boolean } {
  let allowed = 0;
  let denied = 0;
  let any = false;
  for (const t of page.items) {
    for (const cat of t.outcome_mix) {
      if (cat.source !== "decision") continue;
      const v = quantityValue(cat.count);
      if (v === null) continue;
      any = true;
      if (cat.category === "denied") denied += v;
      else if (cat.category === "allowed") allowed += v;
    }
  }
  return { allowed, denied, any };
}

/** Deny rate across the gateway: denied / (allowed + denied), or null when no decisions folded in. */
export function denyRate(page: McpToolPage): Quantity {
  const { allowed, denied, any } = decisionCounts(page);
  const total = allowed + denied;
  if (!any || total === 0) return { value: null, exactness: "exact" };
  return { value: denied / total, exactness: "exact" };
}

/** Call-weighted mean tool error rate; estimated when any contributing tool's calls were sampled. */
export function toolErrorRate(page: McpToolPage): Quantity {
  let weighted = 0;
  let calls = 0;
  let estimated = false;
  for (const t of page.items) {
    const c = quantityValue(t.calls);
    const e = quantityValue(t.error_rate);
    if (c === null || e === null || c <= 0) continue;
    weighted += e * c;
    calls += c;
    if (t.calls.exactness === "estimated" || t.error_rate.exactness === "estimated") estimated = true;
  }
  if (calls === 0) return { value: null, exactness: "exact" };
  return { value: weighted / calls, exactness: estimated ? "estimated" : "exact" };
}

/** The p95 of the busiest tool (percentiles do not sum); null when no tool reported one. */
export function headlineP95(page: McpToolPage): Quantity {
  let busiest: McpToolSummary | null = null;
  let busiestCalls = -1;
  for (const t of page.items) {
    const c = quantityValue(t.calls) ?? 0;
    if (quantityValue(t.latency_p95_ms) === null) continue;
    if (c > busiestCalls) {
      busiest = t;
      busiestCalls = c;
    }
  }
  if (busiest === null) return { value: null, exactness: "exact" };
  return busiest.latency_p95_ms;
}

/**
 * Build the five headline tiles. `unenforcedCalls` is the tool-surface `tools_unenforced` count the
 * caller threads through (it is not on the tools page); null when the surface has not loaded yet.
 */
export function gatewayTiles(page: McpToolPage, unenforcedCalls: number | null): readonly TileModel[] {
  const calls = totalCalls(page);
  const deny = denyRate(page);
  const err = toolErrorRate(page);
  const p95 = headlineP95(page);

  const toTile = (
    key: string,
    label: string,
    q: Quantity,
    format: (q: Quantity) => string,
  ): TileModel => ({
    key,
    label,
    value: format(q),
    measure: quantityMeasure(q),
    empty: quantityValue(q) === null,
  });

  return [
    toTile("calls", "Calls", calls, formatCount),
    toTile("deny", "Deny rate", deny, formatRate),
    toTile("error", "Tool error rate", err, formatRate),
    toTile("p95", "p95 latency", p95, formatMs),
    {
      key: "unenforced",
      label: "Unenforced tools",
      value: unenforcedCalls === null ? "—" : unenforcedCalls.toLocaleString(),
      measure: unenforcedCalls === null ? "unverifiable" : "exact",
      empty: unenforcedCalls === null,
    },
  ];
}

/** A human label for a `tool_outcome` member or a folded `allowed`/`denied` decision category. */
export function outcomeLabel(category: string): string {
  switch (category) {
    case "ok":
      return "OK";
    case "tool_error":
      return "Tool error";
    case "upstream_error":
      return "Upstream error";
    case "timeout":
      return "Timeout";
    case "cancelled":
      return "Cancelled";
    case "allowed":
      return "Allowed";
    case "denied":
      return "Denied";
    case "other":
      return "Other";
    default:
      return category;
  }
}

/** A human label for a `surface_change` drift kind. */
export function surfaceChangeLabel(change: string): string {
  switch (change) {
    case "snapshot":
      return "Snapshot";
    case "added":
      return "Added";
    case "removed":
      return "Removed";
    case "description_changed":
      return "Description changed";
    case "schema_changed":
      return "Schema changed";
    case "other":
      return "Other";
    default:
      return change;
  }
}

/**
 * The status level for a drift row. A `critical` drift (a description/schema change on a previously
 * seen tool) is an error so it stands out; an `unrecorded` drift (inferred from a hash change with
 * no drift event, FR-6.4) is a warning the operator must still see; everything else is neutral.
 */
export function driftLevel(entry: McpToolSurfaceDriftEntry): StatusLevel {
  if (entry.critical) return "error";
  if (!entry.recorded) return "warn";
  return "neutral";
}

/** The visible status text for a drift row, matching {@link driftLevel}. */
export function driftStatusText(entry: McpToolSurfaceDriftEntry): string {
  if (entry.critical) return "Critical";
  if (!entry.recorded) return "Unrecorded";
  return "Recorded";
}

/* ---- Gateway access and Oathera Passport state (telemetry contract 1.7) ---------------------- */

/*
 * Each map is an exhaustive `Record` over the generated contract enum (Requirement 13.3), so a new
 * catalog member without a label fails the type check; the DOM test also walks the generated
 * runtime arrays. There is no fallback to the raw code.
 */

/** Human labels for every `passport_state` member. A missing state is "Not reported", not `other`. */
export const PASSPORT_STATE_LABELS: Readonly<Record<PassportState, string>> = {
  ok: "OK",
  warn: "Warning",
  unavailable: "Unavailable",
  pending: "Pending",
  other: "Other",
};

/** The label shown when an observation did not report a `passport_state`. */
export const PASSPORT_STATE_NOT_REPORTED = "Not reported";

/** The human label for a reported Passport state, or "Not reported" when it is `null`. */
export function passportStateLabel(state: PassportState | null): string {
  return state === null ? PASSPORT_STATE_NOT_REPORTED : PASSPORT_STATE_LABELS[state];
}

/**
 * The status level for a Passport state. A Passport problem (`warn`, `unavailable`) is a WARNING,
 * never an error or a denial: in open mode the call can still be forwarded, and the observation's
 * `outcome` stays authoritative (Requirement 13.2). `pending` is neutral; `other` and a missing state
 * are grey `unknown`, never green.
 */
export function passportStateLevel(state: PassportState | null): StatusLevel {
  switch (state) {
    case "ok":
      return "healthy";
    case "warn":
    case "unavailable":
      return "warn";
    case "pending":
      return "neutral";
    case "other":
    case null:
      return "unknown";
  }
}

/** Human labels for every `gateway_access_outcome` member. */
export const ACCESS_OUTCOME_LABELS: Readonly<Record<GatewayAccessOutcome, string>> = {
  forwarded: "Forwarded",
  refused: "Refused",
  upstream_error: "Upstream error",
  upstream_timeout: "Upstream timeout",
  other: "Other",
};

/** Human labels for every `gateway_source_kind` member. */
export const SOURCE_KIND_LABELS: Readonly<Record<GatewaySourceKind, string>> = {
  verified_agent: "Verified agent",
  claimed_agent: "Claimed agent",
  unidentified: "Unidentified",
  other: "Other",
};

/** Human labels for every `gateway_mode` member. */
export const GATEWAY_MODE_LABELS: Readonly<Record<GatewayMode, string>> = {
  open: "Open",
  enforce: "Enforce",
  observe: "Observe",
  other: "Other",
};
